/* eslint-disable react/forbid-prop-types */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import DashboardChart from 'components/Chart';
import { useGetProjectProposalsReviewStatus } from 'utils/hooks/dashboard';
import Loading from 'components/Loading';
import { formatShowAppliedFilters, statusColors } from 'utils/func/dashboard';
import DashboardFilter from '../DashboardFilter';

function ProjectProposalChartVerticalBar({ programs, organizations }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filters, setFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });

  const [appliedFilters, setAppliedFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });

  const documentStyle = getComputedStyle(document.documentElement);
  const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');

  const { data, isLoading } = useGetProjectProposalsReviewStatus(appliedFilters, {
    refetchOnWindowFocus: false,
    enabled: isLoadedFilterFromLocalStorage,
  });

  const hasActiveFilters =
    appliedFilters.yearProposed?.length > 0 ||
    appliedFilters.organizationId?.length > 0 ||
    appliedFilters.programId?.length > 0;

  const mapLabels = [
    {
      status: 'submit',
    },
    {
      status: 'prequalification',
    },
    {
      status: 'specialization',
    },
    {
      status: 'finance',
    },
    {
      status: 'approved',
    },
    {
      status: 'notQualified',
    },
  ];

  const chartData = useMemo(
    () => ({
      labels: [
        t('dashboard.projectRevise.label.submit'),
        t('dashboard.projectRevise.label.prequalification'),
        t('dashboard.projectRevise.label.specialization'),
        t('dashboard.projectRevise.label.finance'),
        t('dashboard.projectRevise.label.approved'),
        t('dashboard.projectRevise.label.notQualified'),
      ],
      datasets: [
        {
          label: t('dashboard.amountOfProjects'),
          data: Object.values(data),
          backgroundColor: Object.values(statusColors.revise),
          borderColor: Object.values(statusColors.revise),
          borderWidth: 1,
        },
      ],
    }),
    [data, t]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          formatter: (value, ctx) => {
            let sum = 0;
            const dataArr = ctx.chart.data.datasets[0].data;
            dataArr.forEach((element) => {
              sum += element;
            });
            const percentage = ((value * 100) / sum).toFixed(1);
            if (percentage < 0) {
              return null;
            }
            return `${value}`;
          },
          color: textColorSecondary,
          font: {
            weight: 'bold',
            size: 11,
          },
          anchor: 'end',
          align: 'top',
          offset: 4,
        },
        tooltip: {
          callbacks: {
            label(ctx) {
              let sum = 0;
              const dataArr = ctx.chart.data.datasets[0].data;
              dataArr.forEach((element) => {
                sum += element;
              });
              const percentage = ((ctx.raw * 100) / sum).toFixed(1);
              if (percentage <= 0) {
                return null;
              }
              return `${t('dashboard.amountOfProjects')}: ${ctx.raw} (${percentage}%)`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          suggestedMax: Math.max(...Object.values(data)) * 1.1,
          ticks: {
            stepSize: 1,
          },
        },
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 0,
          },
        },
      },
      onHover: (event, activeElements) => {
        const canvas = event.native.target;
        canvas.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const element = elements[0];
          const { index } = element;

          const projectRevisesFilters = {
            programId: appliedFilters?.programId?.length > 0 ? appliedFilters?.programId : [],
            organizationId:
              appliedFilters?.organizationId?.length > 0 ? appliedFilters?.organizationId : [],
            yearProposed:
              appliedFilters?.yearProposed?.length > 0 ? appliedFilters?.yearProposed : [],
          };

          const { status } = mapLabels[index] || {};
          if (status) {
            projectRevisesFilters.statusName = [status];
          }

          localStorage.setItem(
            'admin-projects-revise-filters',
            JSON.stringify(projectRevisesFilters)
          );
          navigate('/projects-revise');
        }
      },
      interaction: {
        intersect: false,
      },
    }),
    [appliedFilters, data, navigate, t, textColorSecondary]
  );

  useEffect(() => {
    setIsLoadedFilterFromLocalStorage(true);
    const savedFilters = localStorage.getItem('admin-dashboard-projects-revise-filters');
    if (!savedFilters) return;

    setFilters(JSON.parse(savedFilters));
    setAppliedFilters(JSON.parse(savedFilters));
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="card p-3 md:p-5">
      <div className="card-header border-bottom-1 surface-border pb-3 mb-4">
        <div className="flex justify-content-between align-items-center">
          <h3 className="text-2xl text-900 font-semibold m-0">
            {t('dashboard.projectRevise.title')}
          </h3>

          <Button
            icon="pi pi-filter"
            label={t('dashboard.filter.label.button')}
            severity={hasActiveFilters ? 'success' : 'secondary'}
            outlined={!hasActiveFilters}
            onClick={() => setShowFilterDialog(true)}
            className="p-button-sm"
          />
        </div>
      </div>

      <DashboardFilter
        showFilterDialog={showFilterDialog}
        onHide={() => setShowFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        setAppliedFilters={setAppliedFilters}
        organizations={organizations}
        programs={programs}
        isSelectOne={{
          yearProposed: false,
          organizationId: false,
          programId: false,
        }}
        localStorageKey="admin-dashboard-projects-revise-filters"
      />

      <div className="flex flex-column align-items-center md:px-4 md:pb-4 gap-2">
        {formatShowAppliedFilters(appliedFilters, organizations, programs, t)}

        <DashboardChart
          data={chartData}
          options={options}
          type="bar"
          className="w-full"
          style={{ height: '400px' }}
        />
      </div>
    </div>
  );
}

ProjectProposalChartVerticalBar.propTypes = {
  programs: PropTypes.array.isRequired,
  organizations: PropTypes.array.isRequired,
};

export default ProjectProposalChartVerticalBar;
